import { useEffect, useState } from 'react';
import { type FieldValues, type UseFormClearErrors, useForm } from 'react-hook-form';
import { type To, useNavigate } from 'react-router-dom';
import {
  type ValidateOfferCodeResponse,
  AppRoutes,
  OfferCodeInputVariables,
} from '@sparrow-card/sparrow-card-constants';
import { validateOfferCode, validateZip } from 'components/Input/templates';
import {
  ApplicationOutcome,
  ErrorType,
  FormName,
  trackApplicationOutcome,
  trackFormError,
  trackFormSubmission,
} from 'utils/clarityEvents';

const getIsOfferFormFilled = (offerCode = '', zipCode = '') =>
  !validateOfferCode(offerCode) && !validateZip(zipCode);

type Props<T> = {
  submitNavigationConfig: (data: FieldValues, validateResult: T) => To;
  validateSubmit: (data: FieldValues) => Promise<T | void>;
  defaultValues?: FieldValues;
};

const useOfferForm = <T extends ValidateOfferCodeResponse>({
  submitNavigationConfig,
  validateSubmit,
  defaultValues,
}: Props<T>) => {
  const navigate = useNavigate();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
    defaultValues,
  });
  const { clearErrors: formClearErrors, handleSubmit, subscribe } = methods;

  const [isFormFilled, setIsFormFilled] = useState(() =>
    getIsOfferFormFilled(
      defaultValues?.[OfferCodeInputVariables.OfferCode],
      defaultValues?.[OfferCodeInputVariables.ZipCode],
    ),
  );
  const [error, setError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(
    () =>
      subscribe({
        formState: { values: true },
        callback: ({ values }) => {
          setIsFormFilled(
            getIsOfferFormFilled(
              values[OfferCodeInputVariables.OfferCode],
              values[OfferCodeInputVariables.ZipCode],
            ),
          );
        },
      }),
    [subscribe],
  );

  const clearErrors: UseFormClearErrors<FieldValues> = (
    name?: string | string[] | readonly string[],
  ) => {
    formClearErrors(name);
    setError(null);
  };

  const onSubmit = async (data: FieldValues) => {
    setIsLoading(true);

    try {
      trackFormSubmission(FormName.OfferCode);

      const validateResult = await validateSubmit(data);

      if (!validateResult) {
        return;
      }

      if (!validateResult.isVelocityCheckPass) {
        trackApplicationOutcome(ApplicationOutcome.PreApprovedLocked);
        navigate(AppRoutes.PreApprovedLocked);

        return;
      }

      if (validateResult.isCodeUsed) {
        trackApplicationOutcome(ApplicationOutcome.OfferCodeUsed);
        navigate(AppRoutes.OfferCodeUsed);

        return;
      }

      if (validateResult.isExpired) {
        trackApplicationOutcome(ApplicationOutcome.OfferCodeExpired);
        navigate(AppRoutes.OfferCodeExpired);

        return;
      }

      if (!validateResult.isOfferValid) {
        trackFormError(FormName.OfferCode, ErrorType.InvalidOffer);
        setError('Offer not found, please try again');
      }

      if (!validateResult.isExpired && validateResult.isOfferValid) {
        navigate(submitNavigationConfig(data, validateResult));
      }
    } catch (e) {
      trackFormError(FormName.OfferCode, ErrorType.SubmissionFailed);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    ...methods,
    clearErrors,
    onSubmit: handleSubmit(onSubmit),
    error,
    isLoading,
    isFormFilled,
  };
};
export default useOfferForm;
